import React, { useState, useEffect } from "react";
import { PageHero, Pagination, ViewToggle, type ViewMode } from "../components/common";
import ResearchCard from "../components/research/ResearchCard";
import { useResearchPosts } from "../hooks/useResearchPosts";
import type { ResearchPost } from "../types";

const FALLBACK_IMAGE = "/images/initiatives/Picture1.png";
const POSTS_PER_PAGE = 6;
const VIEW_STORAGE_KEY = "research-view";

const Research: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  useEffect(() => {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY) as ViewMode | null;
    if (stored === "grid" || stored === "list") setViewMode(stored);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const handleViewChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_STORAGE_KEY, mode);
  };

  const {
    posts: apiPosts,
    totalCount,
    loading,
  } = useResearchPosts(currentPage, POSTS_PER_PAGE);

  const mappedApiPosts: ResearchPost[] = apiPosts.map((post) => ({
    slug: post.id ? String(post.id) : undefined,
    image: post.eng_thumbnail || FALLBACK_IMAGE,
    imageAlt: `${post.eng_title || "Research"} Image`,
    title: post.eng_title || "Research",
    excerpt: post.eng_caption || "",
    date:
      // Prefer created_at when available, then created_date, then published_at
      (post as any).created_at || post.created_date || post.published_at || "",
    tags: Array.isArray(post.eng_tags)
      ? post.eng_tags
          .map((tag) => tag?.tag_name_eng)
          .filter((tagName): tagName is string => Boolean(tagName))
      : [],
    detailsLink: post.id ? `/research/${post.id}` : "#",
    downloadLink: "#",
  }));

  const totalPages = Math.max(
    1,
    Math.ceil((totalCount || mappedApiPosts.length) / POSTS_PER_PAGE)
  );

  return (
    <main id="main-content" role="main" className="page-research">
      <PageHero
        title="AI Literacy Research"
        subtitle="Discover research projects, peer-reviewed publications, and collaborations advancing AI education in Hong Kong and beyond"
        className="blog-hero"
      />

      <section
        className="blog-page-section"
        style={{ padding: "80px 0 100px 0", background: "white" }}
      >
        <div className="container">
          <div
            className="d-flex justify-content-end align-items-center mb-4"
            style={{ gap: "0.5rem" }}
          >
            <span className="text-muted" style={{ fontSize: "0.9rem", marginRight: "0.5rem" }}>
              View
            </span>
            <ViewToggle
              value={viewMode}
              onChange={handleViewChange}
              ariaLabel="Research view: grid or list"
            />
          </div>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading research...</span>
              </div>
            </div>
          ) : (
            <div className="row">
              {mappedApiPosts.length > 0
                ? mappedApiPosts.map((post, index) => (
                    <ResearchCard key={index} post={post} variant={viewMode} />
                  ))
                : null}
            </div>
          )}

          {!loading && totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </section>
    </main>
  );
};

export default Research;
